import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { toast } from "sonner";
import { Download, Maximize2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real, photo-documented cases rendered from Dr. Mishra's clinical vault.
const CASES = [
  {
    img: "/cases/case1.jpg",
    tag: "Implantology & Full-Arch",
    title: "Hybrid Prosthesis — Zygomatic & All-on-6 Implants",
    desc: "Full-arch rehabilitation of a 60-year-old patient with an upper zygomatic implant-supported hybrid prosthesis and a lower All-on-6 — from splinted open-tray impressions and metal framework verification to the final screw-retained hybrid.",
  },
  {
    img: "/cases/case2.jpg",
    tag: "Smile Design & Esthetics",
    title: "Lithium Disilicate Laminates",
    desc: "Minimally invasive e.max veneers on the maxillary incisors, guided by a sliced reduction guide and a precise etch-and-bond protocol for a natural, harmonious smile.",
  },
  {
    img: "/cases/case3.jpg",
    tag: "Full Mouth Rehabilitation",
    title: "Full Mouth Rehabilitation — Fixed Prosthesis",
    desc: "Comprehensive fixed prosthodontic rehabilitation of a worn, collapsed dentition — facebow transfer, diagnostic mock-up on a semi-adjustable articulator, coping trials and definitive cementation across both arches with a post-op OPG follow-up.",
  },
  {
    img: "/cases/case4.jpg",
    tag: "Maxillofacial Prosthodontics",
    title: "Mandibulectomy Rehabilitation with CPD",
    desc: "Post-mandibulectomy (Cantor & Curtis Class I) rehabilitation with a precision cast partial denture — restoring function and appearance through a fully documented workflow.",
  },
  {
    img: "/cases/case5.jpg",
    tag: "Full Mouth Rehabilitation",
    title: "Full Mouth Rehabilitation — Digital Workflow",
    desc: "Rehabilitation of a collapsed, deep-bite dentition using a raised vertical dimension, occlusal splint therapy, CAD/CAM patterns and a semi-precision attachment.",
  },
  {
    img: "/cases/case6.jpg",
    tag: "Implantology",
    title: "Implant-Supported Overdenture",
    desc: "Guided implant placement with a radio-opaque surgical stent, followed by a metal-housing overdenture for a stable, retentive and comfortable result.",
  },
  {
    img: "/cases/case7.jpg",
    tag: "Maxillofacial Prosthodontics",
    title: "Cleft Palate — Hollow Interim Obturator",
    desc: "Management of a cleft-palate defect with a hollow interim obturator using a dual-impression technique to restore speech, swallowing and esthetics.",
  },
  {
    img: "/cases/case8.jpg",
    tag: "Extra-oral Prosthetics",
    title: "Silicone Finger Prosthesis",
    desc: "Custom silicone prosthesis for anonychia with absent phalanges — shade-matched to the donor hand with artificial nails for a lifelike restoration.",
  },
  {
    img: "/cases/case9.jpg",
    tag: "Implantology & Digital",
    title: "Guided Implant — Maxillary Canine",
    desc: "Fully guided implant placement using a 3D-printed surgical guide, with simultaneous bone grafting and a 2-month radiographic follow-up.",
  },
];

const GalleryCard = ({ c, i, onOpen }) => (
  <Reveal delay={(i % 3) * 0.06}>
    <button
      data-testid={`case-card-${i}`}
      onClick={() => onOpen(i)}
      className="group text-left w-full"
    >
      <div className="relative overflow-hidden bg-emerald aspect-[4/3]">
        <img
          src={c.img}
          alt={c.title}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-emerald-dark/0 group-hover:bg-emerald-dark/30 transition-colors duration-500" />
        <div className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center bg-ivory/90 text-emerald opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
          <Maximize2 size={16} />
        </div>
        <div className="absolute inset-0 border border-champagne/30" />
      </div>
      <div className="pt-5">
        <span className="text-champagne-dark text-[11px] uppercase tracking-[0.25em]">{c.tag}</span>
        <h3 className="mt-2 font-display font-light text-xl md:text-2xl text-emerald tracking-tight leading-snug group-hover:text-emerald-light transition-colors duration-300">
          {c.title}
        </h3>
      </div>
    </button>
  </Reveal>
);

export const Cases = () => {
  const [active, setActive] = useState(null);

  const handleDownload = () => {
    toast.success("Preparing your clinical portfolio…");
    window.open(`${API}/portfolio/download`, "_blank");
  };

  const current = active !== null ? CASES[active] : null;

  return (
    <section id="cases" data-testid="cases-section" className="bg-ivory py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-champagne-dark text-xs uppercase tracking-[0.3em] mb-6">
                Clinical Case Gallery
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-emerald leading-[1.05]">
                Real cases. Documented in detail.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-emerald/60 text-base md:text-lg leading-relaxed">
                Nine selected cases from Dr. Mishra&rsquo;s practice — spanning full-arch implant,
                full-mouth and esthetic rehabilitation, maxillofacial and extra-oral prosthetics.
                Tap any case to view the clinical sequence, or download the complete portfolio.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <button
              data-testid="cases-download-portfolio-btn"
              onClick={handleDownload}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-emerald text-ivory px-8 py-4 uppercase tracking-[0.15em] text-sm hover:bg-emerald-light transition-colors duration-300"
            >
              <Download size={16} />
              Download Clinical Portfolio
            </button>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {CASES.map((c, i) => (
            <GalleryCard key={i} c={c} i={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="case-lightbox"
          className="max-w-5xl w-[94vw] p-0 border-0 bg-ivory overflow-hidden max-h-[92vh]"
        >
          {current && (
            <div className="flex flex-col max-h-[92vh]">
              <div className="bg-emerald-dark flex items-center justify-center p-3 overflow-auto">
                <img
                  src={current.img}
                  alt={current.title}
                  className="max-h-[62vh] w-auto object-contain"
                />
              </div>
              <div className="p-7 md:p-9">
                <span className="text-champagne-dark text-[11px] uppercase tracking-[0.25em]">
                  {current.tag}
                </span>
                <DialogTitle className="mt-2 font-display font-light text-2xl md:text-3xl text-emerald tracking-tight">
                  {current.title}
                </DialogTitle>
                <DialogDescription className="mt-3 text-emerald/70 text-sm md:text-base leading-relaxed max-w-2xl">
                  {current.desc}
                </DialogDescription>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Cases;
